import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TIER_CONFIGS } from '@wrm/shared';
import styles from './PricingSection.module.css';

const tiers = [
  {
    key: 'starter',
    name: 'Starter',
    description: 'Essential monitoring for small websites',
    popular: false,
  },
  {
    key: 'professional',
    name: 'Professional',
    description: 'Advanced features for growing businesses',
    popular: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    description: 'Complete solution for organizations',
    popular: false,
  },
] as const;

const CheckIcon = () => (
  <svg className={styles.featureIcon} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

export function PricingSection() {
  return (
    <section id="pricing" className={styles.pricing}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className={styles.title}>Simple, Transparent Pricing</h2>
          <p className={styles.subtitle}>
            Choose the plan that fits your needs. All plans include core security monitoring.
          </p>
        </motion.div>

        <div className={styles.grid}>
          {tiers.map((tier, index) => {
            const config = TIER_CONFIGS[tier.key];
            return (
              <motion.div
                key={tier.key}
                className={`${styles.card} ${tier.popular ? styles.cardPopular : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {tier.popular && (
                  <span className={styles.popularBadge}>Most Popular</span>
                )}

                <h3 className={styles.tierName}>{tier.name}</h3>
                <p className={styles.tierDescription}>{tier.description}</p>

                <div className={styles.priceWrapper}>
                  <span className={styles.price}>${config.priceMonthly}</span>
                  <span className={styles.pricePeriod}>/month</span>
                </div>

                <ul className={styles.features}>
                  <li className={styles.feature}>
                    <CheckIcon />
                    <span>{config.websiteLimit} websites monitored</span>
                  </li>
                  <li className={styles.feature}>
                    <CheckIcon />
                    <span>{config.scansPerMonth === 'unlimited' ? 'Unlimited' : config.scansPerMonth} scans per month</span>
                  </li>
                  <li className={styles.feature}>
                    <CheckIcon />
                    <span>{config.dataRetentionDays === 'unlimited' ? 'Unlimited' : config.dataRetentionDays} days history</span>
                  </li>
                  <li className={styles.feature}>
                    <CheckIcon />
                    <span>{config.pdfReports ? 'Detailed reports included' : 'Basic reports'}</span>
                  </li>
                  {config.emailAlerts && (
                    <li className={styles.feature}>
                      <CheckIcon />
                      <span>Email alerts</span>
                    </li>
                  )}
                </ul>

                <Link
                  to="/sign-up"
                  className={`btn ${tier.popular ? 'btn-primary' : 'btn-secondary'} ${styles.selectButton}`}
                >
                  Get Started
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
